/**
 * 1009 建站舱增强 · host 模式 site.json 扩展键的纯函数:图片位覆盖(`images` / `imagesAuto`)、品牌配色(`theme`)、
 * 导航与按钮文字(`texts`)、产品价格与多图。取值规则与 api `HostSiteExtras` 同一套(改一边记得改另一边):
 *  - 图片位:`images[token]` 非空 = 用这张;`""` = 明确清空;键不在 = 按 logo / 首页首图 / 产品图 N 规则;都没有 = 清空,
 *    **绝不回落到模板原图**(原图是原企业的)。
 *  - 颜色:不设 `theme` = 一个颜色都不发;设了只换主色组(强调色组只在设了强调色时换)。
 *  - 导航与按钮:`texts[token]` 改过的才发;`textsAuto` = 出稿时 AI 按客户业务改写的那些。
 * ★ token 里带点(`{{site.home.image0.src}}`),不能用 `getAt / setAt` 的点路径读写。
 */
import type { HostImageSlot, HostTextSlot, SiteContent, SiteTheme } from "../api/types";

// ── 图片位 ─────────────────────────────────────────────────────────────

export type SlotImageState =
  /** 用户换上的图 */
  | "override"
  /** 出稿时自动配的建议 */
  | "auto"
  /** 用户明确清空 */
  | "cleared"
  /** 跟随 logo / 首图 / 产品图 */
  | "default"
  /** 没有图 */
  | "none";

export interface SlotImage {
  value: string;
  state: SlotImageState;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** 产品的全部图:`image` 在前,再接 `images[]`,去空去重(与 api `productImages` 同口径)。 */
export function productImages(p: Record<string, unknown> | null | undefined): string[] {
  if (!p) return [];
  const out: string[] = [];
  const add = (v: unknown) => {
    const s = str(v);
    if (s && !out.includes(s)) out.push(s);
  };
  add(p.image);
  if (Array.isArray(p.images)) p.images.forEach(add);
  return out;
}

/** 不看覆盖时按 `defaultSource` 取到的图;没有为 `""`。 */
export function defaultSlotImage(content: SiteContent, source: string | null): string {
  if (!source) return "";
  if (source === "logo") return str(content.brand.logo);
  if (source === "hero") return str(content.brand.heroImage);
  const m = /^product:(\d+)$/.exec(source);
  if (m) return productImages(content.products[Number(m[1])] ?? null)[0] ?? "";
  return "";
}

export function slotImage(content: SiteContent, slot: HostImageSlot): SlotImage {
  const images = content.images ?? {};
  if (Object.prototype.hasOwnProperty.call(images, slot.token)) {
    const v = str(images[slot.token]);
    if (!v) return { value: "", state: "cleared" };
    return {
      value: v,
      state: (content.imagesAuto ?? []).includes(slot.token) ? "auto" : "override",
    };
  }
  const d = defaultSlotImage(content, slot.defaultSource);
  return d ? { value: d, state: "default" } : { value: "", state: "none" };
}

/** 换图(`url`)或明确清空(`""`);用户动过就不再是自动建议。纯函数。 */
export function setSlotImage(content: SiteContent, token: string, url: string): SiteContent {
  return {
    ...content,
    images: { ...(content.images ?? {}), [token]: url },
    imagesAuto: (content.imagesAuto ?? []).filter((t) => t !== token),
  };
}

/** 撤销这一格的覆盖(回到跟随 logo / 首图 / 产品图规则)。纯函数。 */
export function resetSlotImage(content: SiteContent, token: string): SiteContent {
  const images = { ...(content.images ?? {}) };
  delete images[token];
  return {
    ...content,
    images,
    imagesAuto: (content.imagesAuto ?? []).filter((t) => t !== token),
  };
}

/** 「从本站已有图选」的候选:logo / 首页首图 / 各产品图 / 已换上的图,去重。 */
export function siteImageChoices(content: SiteContent): { url: string; label: string }[] {
  const out: { url: string; label: string }[] = [];
  const add = (url: unknown, label: string) => {
    const u = str(url);
    if (u && !out.some((c) => c.url === u)) out.push({ url: u, label });
  };
  add(content.brand.logo, "Logo");
  add(content.brand.heroImage, "首页首图");
  content.products.forEach((p, i) => {
    const name = str(p?.name) || `产品 ${i + 1}`;
    productImages(p).forEach((u, j) =>
      add(u, j === 0 ? `产品图 · ${name}` : `${name} · 图 ${j + 1}`)
    );
  });
  Object.values(content.images ?? {}).forEach((u) => add(u, "已上传"));
  return out;
}

export interface SlotPageGroup {
  page: string;
  slots: (HostImageSlot & { index: number })[];
}

/** 按页分组(页序 = `pageOrder`,不在里面的按出现顺序排后面);`index` = 页内「图片 N」的序号(只数配图)。 */
export function groupSlotsByPage(
  slots: readonly HostImageSlot[],
  pageOrder?: readonly string[] | null
): SlotPageGroup[] {
  const groups: SlotPageGroup[] = [];
  for (const s of slots) {
    let g = groups.find((x) => x.page === s.page);
    if (!g) {
      g = { page: s.page, slots: [] };
      groups.push(g);
    }
    const index =
      s.role === "img-item" ? g.slots.filter((x) => x.role === "img-item").length + 1 : 0;
    g.slots.push({ ...s, index });
  }
  const order = pageOrder ?? [];
  const rank = (p: string) => {
    const i = order.indexOf(p);
    return i >= 0 ? i : order.length;
  };
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => rank(a.g.page) - rank(b.g.page) || a.i - b.i)
    .map((x) => x.g);
}

// ── 配色 ────────────────────────────────────────────────────────────────

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#abc` / `#AABBCC` → `#AABBCC`;不合法回 null。 */
export function normalizeHex(v: string): string | null {
  const s = v.trim();
  if (!HEX.test(s)) return null;
  const h = s.slice(1);
  const full = h.length === 3 ? [...h].map((c) => c + c).join("") : h;
  return `#${full.toUpperCase()}`;
}

/** 设主色(必填)/ 强调色(可空);主色空 = 恢复模板配色。纯函数。 */
export function setTheme(content: SiteContent, theme: SiteTheme | null): SiteContent {
  const next = { ...content };
  const primary = theme ? normalizeHex(theme.primary) : null;
  if (!primary) {
    delete next.theme;
    return next;
  }
  const accent = theme?.accent ? normalizeHex(theme.accent) : null;
  next.theme = accent ? { primary, accent } : { primary };
  return next;
}

/**
 * 从像素里取主色(logo 取色):忽略透明 / 近白 / 近黑 / 灰的像素,按 4 位一档量化计数,取最多的那档的平均色。
 * 一个彩色像素都没有回 null(黑白 logo 取不出主色)。纯函数,数据 = RGBA 平铺。
 */
export function dominantColor(data: ArrayLike<number>): string | null {
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i + 3 < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a < 128) continue;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max - min < 40) continue; // 灰 / 白 / 黑
    if (max < 30 || min > 235) continue;
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const cur = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    cur.n += 1;
    cur.r += r;
    cur.g += g;
    cur.b += b;
    buckets.set(key, cur);
  }
  let best: { n: number; r: number; g: number; b: number } | null = null;
  for (const v of buckets.values()) if (!best || v.n > best.n) best = v;
  if (!best) return null;
  const hex = (x: number) =>
    Math.round(x / best.n)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${hex(best.r)}${hex(best.g)}${hex(best.b)}`;
}

// ── 导航与按钮 ──────────────────────────────────────────────────────────

export interface TextGroup {
  /** 同角色、同模板原文的一组(各页导航栏里的「产品中心」是同一项)。 */
  key: string;
  role: HostTextSlot["role"];
  defaultValue: string;
  tokens: string[];
  pages: string[];
}

/** 按「角色 + 模板原文」归组:导航在前、按钮 / 界面文字在后,组内保持画像顺序。 */
export function groupTexts(slots: readonly HostTextSlot[]): TextGroup[] {
  const groups: TextGroup[] = [];
  for (const s of slots) {
    const key = `${s.role}|${s.defaultValue.trim()}`;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, role: s.role, defaultValue: s.defaultValue.trim(), tokens: [], pages: [] };
      groups.push(g);
    }
    g.tokens.push(s.token);
    if (!g.pages.includes(s.page)) g.pages.push(s.page);
  }
  return [...groups.filter((g) => g.role === "nav"), ...groups.filter((g) => g.role !== "nav")];
}

/** 一组当前显示的文字:组里第一个改过的值,没改过 = 模板原文。 */
export function groupTextValue(content: SiteContent, g: TextGroup): string {
  const texts = content.texts ?? {};
  for (const t of g.tokens) if (str(texts[t])) return str(texts[t]);
  return g.defaultValue;
}

/** 这一组是不是出稿时 AI 按客户业务改写的(组里有 token 在 `textsAuto` 里且确有改写值)。 */
export function groupTextIsAuto(content: SiteContent, g: TextGroup): boolean {
  const auto = content.textsAuto ?? [];
  const texts = content.texts ?? {};
  return g.tokens.some((t) => auto.includes(t) && str(texts[t]) !== "");
}

/**
 * 改一组文字(写到组里每个 token);等于模板原文或空 = 撤销覆盖。值真变了才算用户改过 → 从 `textsAuto` 摘掉
 * (重新出稿不再覆盖;api 保存时也按旧草稿重算一遍)。纯函数。
 */
export function setGroupText(content: SiteContent, g: TextGroup, value: string): SiteContent {
  const v = value.trim();
  if (v === groupTextValue(content, g)) return content;
  const texts = { ...(content.texts ?? {}) };
  for (const t of g.tokens) {
    if (!v || v === g.defaultValue) delete texts[t];
    else texts[t] = v;
  }
  const next: SiteContent = { ...content, texts };
  if (content.textsAuto) next.textsAuto = content.textsAuto.filter((t) => !g.tokens.includes(t));
  return next;
}

/** 导航 / 按钮文字每条上限(与 api `HostSiteExtras.TEXT_MAX` 一致)。 */
export const HOST_TEXT_MAX = 30;

// ── 产品价格与多图 ──────────────────────────────────────────────────────

/** 产品多图整组替换:`images = list`,`image = list[0]`(首张即产品卡图,兼容旧数据)。纯函数。 */
export function setProductImages(content: SiteContent, index: number, list: string[]): SiteContent {
  const clean = list.map((u) => u.trim()).filter((u, i, a) => u && a.indexOf(u) === i);
  const products = content.products.map((p, i) =>
    i !== index ? p : { ...p, images: clean, image: clean[0] ?? null }
  );
  return { ...content, products };
}

/** 改价格 / 原价(事实字段:出处记「你填写的」`user:edit`;清空连出处一起去掉)。纯函数。 */
export function setProductPrice(
  content: SiteContent,
  index: number,
  key: "price" | "originalPrice",
  value: string
): SiteContent {
  const v = value.trim();
  const path = `products[${index}].${key}`;
  const bindings = { ...content.bindings };
  if (v) bindings[path] = "user:edit";
  else delete bindings[path];
  const products = content.products.map((p, i) => (i !== index ? p : { ...p, [key]: v }));
  return { ...content, products, bindings };
}
